import { Card, Center, Flex, Heading, Td, Tr, Image, Box, Select, FormLabel } from "@chakra-ui/react";
import React, { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../components/ButtonComp";
import { BiArrowBack } from "react-icons/bi";
import { useNavigate, useLocation } from "react-router-dom";
import CustomInput from "../../components/CustomInput";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CustomToolTip from "../../components/CustomTooltip";
import { FiTrash, FiUpload } from "react-icons/fi";
import TableView from "../../components/TableView";
import { get_order_list } from "../../redux/orderSlice";

const AddDefectiveSchema = Yup.object().shape({
    userid: Yup.string().required("Customer is required"),
    orderid: Yup.string().required("Order ID is required"),
    location: Yup.string().required("Location is required"),
    scheduledate: Yup.string().required("Schedule date is required"),
    scheduletime: Yup.string().required("Schedule time is required"),
    products: Yup.array().of(
        Yup.object().shape({
            prod_code: Yup.string().required("Product code is required"),
            quantity: Yup.number()
                .required("Quantity is required")
                .min(1, "Quantity must be at least 1"),
            cyld_type: Yup.string().required("Cylinder type is required"),
            type: Yup.string().required("Type is required"),
            description: Yup.string().required("Description is required"),
            image: Yup.mixed().nullable(), // Image is optional
        })
    ),
});

const AddDefective = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    // Redux state
    const { order_list, loading: ordersLoading } = useSelector((state) => state.orders);
    const [defectiveList, setDefectiveList] = useState([]);
    const [selectedOrderProducts, setSelectedOrderProducts] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState("");
    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState("");

    const initialValues = {
        userid: "",
        orderid: "",
        location: "",
        scheduledate: "",
        scheduletime: "",
        products: [
            {
                prod_code: "",
                quantity: "",
                unit_rate: "",
                cyld_type: "IOCL",
                type: "DEFECTIVE",
                description: "",
                image: null,
            },
        ],
    };

    // Fetch orders on component mount
    useEffect(() => {
        const fetchOrders = async () => {
            try {
                await dispatch(get_order_list({
                    page: 1,
                    rows: 1000,
                    type: "4",
                    action: "list"
                })).unwrap();
            } catch (error) {
                console.error("Error fetching orders:", error);
                toast.error("Failed to fetch orders");
            }
        };

        fetchOrders();
    }, [dispatch]);

    // Extract unique customers when order_list changes
    useEffect(() => {
        if (order_list && order_list.length > 0) {
            const uniqueCustomers = [];
            const customerMap = new Map();

            order_list.forEach(order => {
                if (!customerMap.has(order.userid)) {
                    customerMap.set(order.userid, {
                        userid: order.userid,
                        customer_name: order.customer_name,
                        email: order.email,
                        mobile: order.mobile
                    });
                    uniqueCustomers.push({
                        userid: order.userid,
                        customer_name: order.customer_name,
                        email: order.email,
                        mobile: order.mobile
                    });
                }
            });

            setCustomers(uniqueCustomers);
        }
    }, [order_list]);

    // Filter orders when customer is selected
    useEffect(() => {
        if (selectedCustomerId && order_list) {
            const customerOrders = order_list.filter(order => order.userid === selectedCustomerId);
            setFilteredOrders(customerOrders);
        } else {
            setFilteredOrders([]);
        }
    }, [selectedCustomerId, order_list]);

    const handleCustomerChange = (customerId, setFieldValue) => {
        setSelectedCustomerId(customerId);
        setFieldValue("userid", customerId);
        setFieldValue("orderid", "");

        const customerOrder = order_list.find(order => order.userid === customerId);
        if (customerOrder) {
            setSelectedAddress(customerOrder.shipping_address);
            setFieldValue("location", customerOrder.shipping_address);
        } else {
            setSelectedAddress("");
            setFieldValue("location", "");
        }
    };

    const handleOrderChange = async (orderId, setFieldValue, values) => {
        setFieldValue("orderid", orderId);

        try {
            // Make API call to get detailed order information
            const formData = new FormData();
            formData.append("action", "order_by_id");
            formData.append("orderid", orderId);
            formData.append("type", "4");

            // If you have the internal ID, you can also send it
            const selectedOrder = filteredOrders.find(order => order.orderid === orderId);
            if (selectedOrder && selectedOrder.id) {
                formData.append("id", selectedOrder.id);
            }

            const response = await fetch("https://dev.gaswale.com/api/admin/orders.php", {
                method: "POST",
                body: formData,
            });

            const result = await response.json();

            if (response.ok && result.success) {
                const orderDetails = result.reponselist || result;

                setSelectedAddress(orderDetails.shipping_address);
                setFieldValue("location", orderDetails.shipping_address);

                // Extract products from the detailed response
                let orderProducts = [];

                if (Array.isArray(orderDetails.details) && orderDetails.details.length > 0) {
                    // Filter products to only include delivered ones with delivered_quantity > 0
                    orderProducts = orderDetails.details
                        .filter(detail => detail.delivered_quantity > 0)
                        .map(detail => ({
                            productid: detail.productid || "",
                            product_name: detail.product_name || "",
                            quantity: detail.delivered_quantity || "0", // Use delivered quantity
                            cylinder_type: detail.type || extractCylinderType(detail.product_name) || "IOCL",
                            delivered_quantity: detail.delivered_quantity || "0"
                        }));
                }

                setSelectedOrderProducts(orderProducts);

                if (orderProducts.length > 0) {
                    const newProducts = [...values.products];
                    newProducts[0] = {
                        ...newProducts[0],
                        prod_code: orderProducts[0].productid,
                        quantity: orderProducts[0].delivered_quantity || "",
                        cyld_type: orderProducts[0].cylinder_type
                    };
                    setFieldValue("products", newProducts);
                } else {
                    // Reset product fields if no delivered products
                    const newProducts = [...values.products];
                    newProducts[0] = {
                        ...newProducts[0],
                        prod_code: "",
                        quantity: "",
                        cyld_type: "IOCL"
                    };
                    setFieldValue("products", newProducts);
                    toast.info("No delivered products found for this order");
                }
            } else {
                toast.error("Failed to fetch order details");
                setSelectedAddress("");
                setFieldValue("location", "");
                setSelectedOrderProducts([]);
            }
        } catch (error) {
            console.error("Error fetching order details:", error);
            toast.error("Error fetching order details");
            setSelectedAddress("");
            setFieldValue("location", "");
            setSelectedOrderProducts([]);
        }
    };

    const extractCylinderType = (productName) => {
        if (!productName) return "IOCL";
        if (productName.includes("IOCL")) return "IOCL";
        if (productName.includes("HPCL")) return "HPCL";
        if (productName.includes("RAZOR")) return "RAZOR";
        return "IOCL";
    };

    const handleImageUpload = (e, index, setFieldValue) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setFieldValue(`products[${index}].image`, {
                    file,
                    preview: event.target.result
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = (index, setFieldValue) => {
        setFieldValue(`products[${index}].image`, null);
    };

    const handleSubmit = useCallback(async (values) => {
        try {
            setLoading(true);

            const formData = new FormData();
            formData.append("userid", values.userid);
            formData.append("action", "create");
            formData.append("orderid", values.orderid);
            formData.append("location", values.location);
            formData.append("scheduledate", values.scheduledate);
            formData.append("scheduletime", values.scheduletime);
            
            // Add only required product details
            values.products.forEach((product, index) => {
                formData.append(`prod_code[${index}]`, product.prod_code);
                formData.append(`quantity[${index}]`, product.quantity);
                formData.append(`unit_rate[${index}]`, product.unit_rate);
                formData.append(`gst[${index}]`,"18");
                formData.append(`cyld_type[${index}]`, product.cyld_type);
                formData.append(`type[${index}]`, product.type);
                formData.append(`description[${index}]`, product.description);

                // Only include image if it exists
                if (product.image?.file) {
                    formData.append(`image[${index}]`, product.image.file);
                }
            });

            const response = await fetch("https://dev.gaswale.com/api/admin/defective_return.php", {
                method: "POST",
                body: formData,
            });

            const result = await response.json();

            if (response.ok && result.success) {
                toast.success(result.message || "Defective return added successfully");
                setDefectiveList([]);
                navigate(-1);
            } else {
                toast.error(result.message || "Failed to add defective return");
            }
        } catch (error) {
            console.error("Defective return submission error:", error);
            toast.error(error.message || "An error occurred while submitting");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    const onAddDefectiveReturn = (values, { resetForm }) => {
        const newDefectiveItem = {
            userid: values.userid,
            orderid: values.orderid,
            location: values.location,
            scheduledate: values.scheduledate,
            scheduletime: values.scheduletime,
            products: values.products.map(product => ({
                prod_code: product.prod_code,
                quantity: product.quantity,
                unit_rate: product.unit_rate,
                cyld_type: product.cyld_type,
                type: product.type,
                description: product.description,
                image: product.image,
            })),
        };
    
        if (defectiveList.length > 0) {
            // 🚨 Restrict to only one customer
            if (defectiveList[0].userid !== values.userid) {
                toast.error("You can add defective returns for only one customer at a time.");
                return;
            }
    
            // Merge products if same customer
            const updatedList = [...defectiveList];
            updatedList[0].products = [
                ...updatedList[0].products,
                ...newDefectiveItem.products
            ];
            setDefectiveList(updatedList);
        } else {
            // First time: add the customer
            setDefectiveList([newDefectiveItem]);
        }
    
        // Reset form but keep customer/order info
        resetForm({
            values: {
                ...initialValues,
                userid: values.userid,
                orderid: values.orderid,
                location: values.location
            }
        });
    };
    

    const handleRemoveDefective = (index) => {
        setDefectiveList(prev => prev.filter((_, i) => i !== index));
    };

    const customerOptions = customers.map(customer => ({
        value: customer.userid,
        label: `${customer.customer_name}`
    }));

    const orderOptions = filteredOrders.map(order => ({
        value: order.orderid,
        label: `${order.orderid}`
    }));

    return (
        <>
            <Card
                display={"flex"}
                flexDirection={"row"}
                alignItems={"center"}
                justify={"space-between"}
                paddingInline={5}
                paddingBlock={2}
            >
                <Heading fontSize={"xl"}>Add Defective Return</Heading>
                <CustomButton
                    icon={<BiArrowBack color="#fff" />}
                    title={"Back"}
                    height={"30px"}
                    b_color={"#000"}
                    onClick={() => navigate(-1)}
                />
            </Card>

            {defectiveList.length > 0 && (
                <Card display={"flex"} paddingInline={3} paddingBlock={5} gap={5}>
                    <TableView
                        headData={[
                            "S.No.",
                            "Order ID",
                            "User ID",
                            "Location",
                            "Schedule Date",
                            "Schedule Time",
                            "Product Code",
                            "Quantity",
                            "Type",
                            "Image",
                            "Action",
                        ]}
                        body={defectiveList.flatMap((item, index) =>
                            item.products.map((prod, i) => (
                                <Tr key={`${index}-${i}`}>
                                    <Td textAlign="center">{index + 1}</Td>
                                    <Td textAlign="center">{item.orderid}</Td>
                                    <Td textAlign="center">{item.userid}</Td>
                                    <Td textAlign="center">{item.location}</Td>
                                    <Td textAlign="center">{item.scheduledate}</Td>
                                    <Td textAlign="center">{item.scheduletime}</Td>
                                    <Td textAlign="center">{prod.prod_code}</Td>
                                    <Td textAlign="center">{prod.quantity}</Td>
                                    <Td textAlign="center">{prod.type}</Td>
                                    <Td textAlign="center">
                                        {prod.image?.preview && (
                                            <Image
                                                src={prod.image.preview}
                                                alt="Defective product"
                                                boxSize="50px"
                                                objectFit="cover"
                                            />
                                        )}
                                    </Td>
                                    <Td textAlign="center">
                                        <Flex justify={"center"} gap={4}>
                                            <CustomToolTip
                                                button={
                                                    <FiTrash
                                                        size={20}
                                                        cursor={"pointer"}
                                                        color="#F00"
                                                        onClick={() => handleRemoveDefective(index)}
                                                    />
                                                }
                                                mess={"Delete Defective Return"}
                                            />
                                        </Flex>
                                    </Td>
                                </Tr>
                            ))
                        )}
                    />

                    <Flex justifyContent={"flex-end"}>
                        <CustomButton
                            title={"Submit Defective Returns"}
                            onClick={() => handleSubmit({
                                ...defectiveList[0],
                                products: defectiveList.flatMap(item => item.products)
                            })}
                            loading={loading}
                        />
                    </Flex>
                </Card>
            )}

            <Formik
                initialValues={initialValues}
                validationSchema={AddDefectiveSchema}
                onSubmit={onAddDefectiveReturn}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    isSubmitting,
                    isValid,
                    submitForm,
                    resetForm
                }) => {
                    // Check if current product is valid
                    const currentProduct = values.products && values.products.length > 0 ? values.products[0] : {};
                    const isProductValid = (
                        currentProduct.prod_code &&
                        currentProduct.quantity &&
                        currentProduct.cyld_type &&
                        currentProduct.type &&
                        currentProduct.description
                    );

                    return (
                        <Form>
                            <Card padding={5}>
                                <CustomInput
                                    label1={"Select Customer"}
                                    value1={values.userid}
                                    onChange1={(e) => {
                                        handleCustomerChange(e.target.value, setFieldValue);
                                    }}
                                    onBlur1={handleBlur("userid")}
                                    placeholder1={"Select a customer"}
                                    error1={touched.userid && errors.userid}
                                    required1={true}
                                    select1={true}
                                    options1={[
                                        { value: "", label: "Select Customer" },
                                        ...customerOptions
                                    ]}
                                    loading1={ordersLoading}
                                    label2={"Order ID"}
                                    value2={values.orderid}
                                    onChange2={(e) => handleOrderChange(e.target.value, setFieldValue, values)}
                                    onBlur2={handleBlur("orderid")}
                                    placeholder2={selectedCustomerId ? "Select order ID" : "First select a customer"}
                                    error2={touched.orderid && errors.orderid}
                                    required2={true}
                                    select2={true}
                                    options2={[
                                        { value: "", label: selectedCustomerId ? "Select Order" : "First select customer" },
                                        ...orderOptions
                                    ]}
                                    disabled2={!selectedCustomerId}
                                />

                                <CustomInput
                                    label1={"Location"}
                                    value1={values.location}
                                    onChange1={handleChange("location")}
                                    onBlur1={handleBlur("location")}
                                    placeholder1={"Enter location"}
                                    error1={touched.location && errors.location}
                                    required1={true}
                                    readOnly1={selectedAddress}
                                    label2={"Schedule Date"}
                                    value2={values.scheduledate}
                                    onChange2={handleChange("scheduledate")}
                                    onBlur2={handleBlur("scheduledate")}
                                    placeholder2={"Select schedule date"}
                                    error2={touched.scheduledate && errors.scheduledate}
                                    required2={true}
                                    type2={"date"}
                                />

                                <CustomInput
                                    label1={"Schedule Time"}
                                    value1={values.scheduletime}
                                    onChange1={handleChange("scheduletime")}
                                    onBlur1={handleBlur("scheduletime")}
                                    error1={touched.scheduletime && errors.scheduletime}
                                    required1={true}
                                    select1={true}
                                    options1={[
                                        { value: "", label: "Select Schedule Time" },
                                        { value: "02:00 PM to 08:00 PM", label: "02:00 PM to 08:00 PM" },
                                        { value: "08:00 AM to 02:00 PM", label: "08:00 AM to 02:00 PM" },
                                    ]}
                                />

                                {selectedOrderProducts.length > 0 && (
                                    <Box mb={4}>
                                        <FormLabel>Select Product</FormLabel>
                                        <Select
                                            value={values.products[0].prod_code}
                                            onChange={(e) => {
                                                const selectedProduct = selectedOrderProducts.find(
                                                    p => p.productid === e.target.value
                                                );
                                                if (selectedProduct) {
                                                    const newProducts = [...values.products];
                                                    newProducts[0] = {
                                                        ...newProducts[0],
                                                        prod_code: selectedProduct.productid,
                                                        quantity: selectedProduct.delivered_quantity || "",
                                                        cyld_type: selectedProduct.cylinder_type
                                                    };
                                                    setFieldValue("products", newProducts);
                                                }
                                            }}
                                            placeholder="Select a delivered product"
                                        >
                                            {selectedOrderProducts.map(product => (
                                                <option key={product.productid} value={product.productid}>
                                                    {`${product.productid} - ${product.product_name}`}
                                                </option>
                                            ))}
                                        </Select>


                                    </Box>
                                )}

                                <CustomInput    
                                    label1={"Unit Price"}
                                    value1={values.products[0].unit_rate}
                                    onChange1={(e) => {
                                        const newProducts = [...values.products];
                                        newProducts[0].unit_rate = e.target.value;
                                        setFieldValue("products", newProducts);
                                    }}
                                    onBlur1={handleBlur(`products[0].unit_rate`)}
                                    placeholder1={"Enter unit price"}
                                    error1={
                                        touched.products?.[0]?.unit_rate &&
                                        errors.products?.[0]?.unit_rate
                                    }
                                    required1={true}
                                />

                                <Card p={4} mb={4} borderWidth="1px">
                                    <Heading fontSize="md" mb={4}>Product Details</Heading>
                                    <CustomInput
                                        label1={"Product Code"}
                                        value1={values.products[0].prod_code}
                                        onChange1={(e) => {
                                            const newProducts = [...values.products];
                                            newProducts[0].prod_code = e.target.value;
                                            setFieldValue("products", newProducts);
                                        }}
                                        onBlur1={handleBlur(`products[0].prod_code`)}
                                        placeholder1={"Enter product code or select from order"}
                                        error1={
                                            touched.products?.[0]?.prod_code &&
                                            errors.products?.[0]?.prod_code
                                        }
                                        required1={true}
                                        readOnly1={selectedOrderProducts.length > 0}
                                    />

                                    <CustomInput
                                        label1={"Quantity"}
                                        value1={values.products[0].quantity}
                                        onChange1={(e) => {
                                            const newProducts = [...values.products];
                                            newProducts[0].quantity = e.target.value;
                                            setFieldValue("products", newProducts);
                                        }}
                                        onBlur1={handleBlur(`products[0].quantity`)}
                                        placeholder1={"Enter quantity"}
                                        error1={
                                            touched.products?.[0]?.quantity &&
                                            errors.products?.[0]?.quantity
                                        }
                                        required1={true}
                                        max={values.products[0].prod_code ?
                                            selectedOrderProducts.find(p => p.productid === values.products[0].prod_code)?.delivered_quantity || ""
                                            : ""}
                                    />

                                    <CustomInput
                                        label1={"Cylinder Type"}
                                        value1={values.products[0].cyld_type}
                                        onChange1={(e) => {
                                            const newProducts = [...values.products];
                                            newProducts[0].cyld_type = e.target.value;
                                            setFieldValue("products", newProducts);
                                        }}
                                        onBlur1={handleBlur(`products[0].cyld_type`)}
                                        placeholder1={"Enter cylinder type"}
                                        error1={
                                            touched.products?.[0]?.cyld_type &&
                                            errors.products?.[0]?.cyld_type
                                        }
                                        required1={true}
                                        readOnly1={selectedOrderProducts.length > 0}
                                    />

                                    <CustomInput
                                        label1={"Type"}
                                        value1={values.products[0].type}
                                        onChange1={(e) => {
                                            const newProducts = [...values.products];
                                            newProducts[0].type = e.target.value;
                                            setFieldValue("products", newProducts);
                                        }}
                                        onBlur1={handleBlur(`products[0].type`)}
                                        select1={true}
                                        options1={[
                                            { value: "Underweight", label: "Underweight" },
                                            { value: "Leakage", label: "Leakage" },
                                            { value: "Damaged", label: "Damaged" },
                                            { value: "Other", label: "Other" },
                                        ]}
                                        error1={
                                            touched.products?.[0]?.type &&
                                            errors.products?.[0]?.type
                                        }
                                        required1={true}
                                    />

                                    <CustomInput
                                        label1={"Description"}
                                        value1={values.products[0].description}
                                        onChange1={(e) => {
                                            const newProducts = [...values.products];
                                            newProducts[0].description = e.target.value;
                                            setFieldValue("products", newProducts);
                                        }}
                                        onBlur1={handleBlur(`products[0].description`)}
                                        placeholder1={"Enter description"}
                                        error1={
                                            touched.products?.[0]?.description &&
                                            errors.products?.[0]?.description
                                        }
                                        required1={true}
                                    />

                                    {/* Image Upload Section */}
                                    <Box mt={4}>
                                        <Heading fontSize="sm" mb={2}>Product Image (Optional)</Heading>
                                        {values.products[0].image?.preview ? (
                                            <Box position="relative">
                                                <Image
                                                    src={values.products[0].image.preview}
                                                    alt="Product preview"
                                                    boxSize="150px"
                                                    objectFit="cover"
                                                    borderRadius="md"
                                                />
                                                <Box
                                                    position="absolute"
                                                    top={1}
                                                    right={1}
                                                    bg="white"
                                                    borderRadius="full"
                                                    p={1}
                                                    cursor="pointer"
                                                    onClick={() => handleRemoveImage(0, setFieldValue)}
                                                >
                                                    <FiTrash color="red" />
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Box
                                                border="2px dashed"
                                                borderColor="gray.300"
                                                borderRadius="md"
                                                p={4}
                                                textAlign="center"
                                                cursor="pointer"
                                                _hover={{ borderColor: "blue.500" }}
                                            >
                                                <label htmlFor="product-image-0" style={{ cursor: "pointer" }}>
                                                    <Flex direction="column" align="center">
                                                        <FiUpload size={24} />
                                                        <Box mt={2}>Upload Image</Box>
                                                        <input
                                                            id="product-image-0"
                                                            type="file"
                                                            accept="image/*"
                                                            style={{ display: "none" }}
                                                            onChange={(e) => handleImageUpload(e, 0, setFieldValue)}
                                                        />
                                                    </Flex>
                                                </label>
                                            </Box>
                                        )}
                                    </Box>
                                </Card>

                                <Center marginBlock={5} gap={10}>
                                    <CustomButton
                                        title={"Add Defective Return"}
                                        onClick={() => {
                                            submitForm();
                                        }}
                                        disabled={isSubmitting || !isValid || !isProductValid}
                                    />
                                    <CustomButton
                                        title={"Submit All"}
                                        onClick={() => {
                                            if (defectiveList.length > 0) {
                                                handleSubmit({
                                                    ...defectiveList[0],
                                                    products: defectiveList.flatMap(item => item.products)
                                                });
                                            } else {
                                                submitForm();
                                            }
                                        }}
                                        disabled={isSubmitting || (!isValid && defectiveList.length === 0)}
                                    />
                                </Center>
                            </Card>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};

export default AddDefective;    